import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { legibleOn } from '../../lib/contrast'
import { tierColor } from '../../lib/badges'
import BadgeMedal from './BadgeMedal'
import { useBadgeText } from './BadgeCard'

function formatDate(iso, lng) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(lng, { day: 'numeric', month: 'long', year: 'numeric' })
}

// Dettaglio di una famiglia: tutti i livelli, quali hai gia', quanto manca e quanti nella lega l'hanno ottenuto
export default function BadgeSheet({ family, onClose, onShare }) {
  const tc = useThemeColors()
  const { t, i18n } = useTranslation()
  const txt = useBadgeText()
  const insets = useSafeAreaInsets()
  if (!family) return null
  const hidden = family.secret && !family.earned

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[s.header, { borderBottomColor: tc.border }]}>
            <Text style={[s.headerTitle, { color: tc.text }]} accessibilityRole="header" numberOfLines={2}>
              {hidden ? t('badges.secret_title') : txt.familyTitle(family)}
            </Text>
            <Pressable onPress={onClose} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
            {hidden ? (
              <View style={s.secret}>
                <BadgeMedal state="secret" color={tc.textMuted} size={72} />
                <Text style={[s.secretText, { color: tc.textMuted }]}>{t('badges.secret_hint')}</Text>
              </View>
            ) : family.levels.map(l => {
              const color = tierColor(l.level, family.maxLevel)
              const isNext = family.next?.id_badge === l.id_badge
              const pct = l.nMembers > 0 ? Math.round((l.nEarned / l.nMembers) * 100) : 0
              return (
                <View key={l.id_badge} style={[s.level, { backgroundColor: l.earned ? color + '14' : tc.bgCard2, borderColor: l.earned ? color + '66' : tc.border }]}>
                  <BadgeMedal icon={family.icon} color={color} size={52} state={l.earned ? 'earned' : 'locked'} />
                  <View style={{ flex: 1, gap: 3 }}>
                    <Text style={[s.levelTitle, { color: tc.text }]}>{txt.levelTitle(family, l)}</Text>
                    <Text style={[s.levelDesc, { color: tc.textMuted }]}>{txt.levelDesc(family, l)}</Text>
                    {l.earned ? (
                      <Text style={[s.levelStatus, { color: legibleOn(color, tc.bgCard, 3) }]}>
                        {family.yearly && l.seasons > 1
                          ? t('badges.seasons', { count: l.seasons })
                          : t('badges.earned_on', { date: formatDate(l.awardedAt, i18n.language) })}
                      </Text>
                    ) : isNext && family.value > 0 ? (
                      <View style={{ gap: 4, marginTop: 2 }}>
                        <View style={[s.barBg, { backgroundColor: tc.border }]}>
                          <View style={[s.barFill, { backgroundColor: color, width: `${Math.round(family.ratio * 100)}%` }]} />
                        </View>
                        <Text style={[s.levelStatus, { color: tc.textMuted }]}>{t('badges.progress', { value: family.value, threshold: l.threshold })}</Text>
                      </View>
                    ) : null}
                    <Text style={[s.rarity, { color: tc.textMuted }]}>
                      {l.nEarned > 0 ? t('badges.rarity', { pct, n: l.nEarned, m: l.nMembers }) : t('badges.rarity_none')}
                    </Text>
                    {l.earned && onShare && (
                      <Pressable onPress={() => onShare(family, l)} accessibilityRole="button" accessibilityLabel={`${t('badges.share')}: ${txt.levelTitle(family, l)}`} style={s.shareBtn}>
                        <MaterialCommunityIcons name="share-variant-outline" size={16} color={tc.rosaText} />
                        <Text style={[s.shareText, { color: tc.rosaText }]}>{t('badges.share')}</Text>
                      </Pressable>
                    )}
                  </View>
                </View>
              )
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '85%' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 16, borderBottomWidth: 0.5 },
  headerTitle: { flex: 1, fontFamily: fonts.condensedBold, fontSize: 20 },
  closeBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  secret: { alignItems: 'center', gap: 12, paddingVertical: 24 },
  secretText: { fontSize: 14, textAlign: 'center' },
  level: { flexDirection: 'row', gap: 12, borderWidth: 1, borderRadius: 14, padding: 12 },
  levelTitle: { fontFamily: fonts.condensedBold, fontSize: 16 },
  levelDesc: { fontSize: 13, lineHeight: 18 },
  levelStatus: { fontSize: 12, fontFamily: fonts.condensedBold },
  rarity: { fontSize: 12, lineHeight: 16, marginTop: 2 },
  shareBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 44, alignSelf: 'flex-start' },
  shareText: { fontSize: 13, fontFamily: fonts.condensedBold },
  barBg: { height: 5, borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
})
